import { useMemo, useState } from "react";
import type { ApiKeyIssue, UserDto } from "@fortex/api";
import { memoryLoader } from "@fortex/api";
import { Chip, Dialog, Input } from "@fortex/map";
import type { GridColumn } from "@fortex/map";
import { useApp, useAuth } from "../../app/context";
import { useLoad } from "../../app/useLoad";
import { features } from "../../auth/store";
import { CrudPage } from "../shared/CrudPage";
import { userField } from "../users/UsersPage";
import { ApiKeyForm, apiKeyDraftOf, saveApiKeyUser, validateApiKeyDraft } from "./ApiKeyForm";
import type { ApiKeyUserDraft } from "./ApiKeyForm";

export function isApiKeyUser(user: UserDto): boolean {
  return user.type === "ApiKey";
}

export function apiKeyUserField(row: UserDto, field: string): unknown {
  return field === "Name" ? (row.name ?? "") : userField(row, field);
}

export function IssuedKeyDialog({ issue, onClose }: { issue: ApiKeyIssue | null; onClose: () => void }) {
  const { labels, toast } = useApp();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (issue === null) return;

    try {
      await navigator.clipboard.writeText(issue.apiKey);
      setCopied(true);
      toast("success", labels.apiKeyCopied);
    } catch {
      setCopied(false);
    }
  };

  const close = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={issue !== null} title={labels.apiKeyIssuedTitle} text={labels.apiKeyIssuedText} confirmLabel={copied ? labels.apiKeyCopied : labels.copy} cancelLabel={labels.close} onConfirm={() => void copy()} onCancel={close} dataFxmap="api-key-issued-dialog">
      {issue !== null ? <Input readOnly value={issue.apiKey} mono onFocus={event => event.target.select()} data-fxmap="api-key-value" /> : null}
    </Dialog>
  );
}

export function ApiKeysPage() {
  const { api, labels, lang } = useApp();
  const canEdit = useAuth(a => a.can(features.userAdmin, "Full"));
  const canEditRoles = useAuth(a => a.can(features.role, "Full"));
  const users = useLoad(signal => api.users(null, null, signal), []);
  const rows = useMemo(() => (users.data ?? []).filter(isApiKeyUser), [users.data]);
  const load = useMemo(() => memoryLoader<UserDto>(rows, apiKeyUserField), [rows]);
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "hu-HU", { dateStyle: "short", timeStyle: "short" }), [lang]);
  const [issue, setIssue] = useState<ApiKeyIssue | null>(null);

  const columns = useMemo<GridColumn<UserDto>[]>(
    () => [
      { key: "Name", header: labels.apiKeyName, field: "Name", sortable: true, mono: true, filter: { kind: "text" }, render: row => row.name ?? "" },
      { key: "FullName", header: labels.fullName, field: "FullName", sortable: true, filter: { kind: "text" }, render: row => row.fullName ?? "" },
      { key: "Active", header: labels.userStatus, field: "Active", sortable: true, width: "120px", filter: { kind: "boolean" }, render: row => <Chip tone={row.active ? "success" : "neutral"}>{row.active ? labels.active : labels.inactive}</Chip> }
    ],
    [labels]
  );

  return (
    <>
      <CrudPage<UserDto, ApiKeyUserDraft>
        dataFxmap="api-keys-page"
        columns={columns}
        rowKey={row => row.id}
        load={load}
        defaultSortField="Name"
        quickFields={["Name", "FullName"]}
        canEdit={canEdit}
        newLabel={labels.newApiKeyUser}
        emptyDraft={() => apiKeyDraftOf(null)}
        toDraft={row => apiKeyDraftOf(row)}
        detailTitle={editing => (editing.isNew ? labels.newApiKeyUser : labels.editApiKeyUser)}
        detailSubtitle={editing => editing.draft.user?.name ?? null}
        form={(draft, setDraft, errors) => <ApiKeyForm api={api} labels={labels} draft={draft} setDraft={setDraft} errors={errors} canEditRoles={canEditRoles} readOnly={!canEdit} dateFormat={dateFormat} onIssued={setIssue} />}
        validate={draft => validateApiKeyDraft(draft, labels)}
        save={draft => saveApiKeyUser(api, draft, setIssue)}
        remove={canEdit ? async draft => (draft.user === null ? undefined : api.deleteUser(draft.user.id)) : null}
        deleteText={draft => labels.deleteApiKeyUserText.replace("{name}", draft.name)}
        savedText={labels.apiKeyUserSaved}
        deletedText={labels.apiKeyUserDeleted}
        exportName="api-keys"
        reloadToken={rows.length}
        onReload={users.reload}
      />
      <IssuedKeyDialog issue={issue} onClose={() => setIssue(null)} />
    </>
  );
}
